/**
 * Partnership API Functions
 *
 * API functions for company partnership operations
 */

import type {
  CompanyPartnership,
  CreatePartnershipDto,
  UpdatePartnershipDto,
  PartnershipStatus,
  PartnershipType,
} from '@lognai/plugin-admin';
import { apiFetch, buildQueryString } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';

const BASE_URL = '/api/companies';

/**
 * Partnership with enriched data. The partner company is populated under
 * `partnerCompany` (populate-by-id `as: 'partnerCompany'`), matching the
 * backend EnrichedPartnership.
 */
export interface EnrichedPartnership extends CompanyPartnership {
  partnerCompany?: {
    id: string;
    name: string;
    slug: string;
  };
}

/**
 * Paginated partnership list as returned by the HTTP endpoint:
 * `{ items, pagination: { page, limit, total, totalPages } }`.
 */
export interface PaginatedPartnershipList {
  items: EnrichedPartnership[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
}

/**
 * Partnership filter fields
 */
export interface PartnershipFilter {
  status?: PartnershipStatus;
  partnerType?: PartnershipType;
}

/**
 * Partnership query params
 */
export type PartnershipQueryParams = ListQueryParams<PartnershipFilter>;

/**
 * Get list of partnerships for a company
 */
export const listPartnerships = async (
  companyId: string,
  params?: PartnershipQueryParams
): Promise<PaginatedPartnershipList> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedPartnershipList>(
    `${BASE_URL}/${companyId}/partnerships${query}`
  );
};

/**
 * Get a single partnership by ID
 */
export const getPartnership = async (
  companyId: string,
  partnershipId: string
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`
  );
};

/**
 * Create a partnership
 */
export const createPartnership = async (
  companyId: string,
  dto: CreatePartnershipDto
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(`${BASE_URL}/${companyId}/partnerships`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/** Network access level granted to a partner relationship. */
export type PartnerAccessLevel = 'NONE' | 'READ_ONLY' | 'READ_WRITE' | 'FULL';

/** Input for adding an existing user (by email) as a partner of the company. */
export interface AddPartnerUserDto {
  email: string;
  accessLevel: PartnerAccessLevel;
}

/**
 * Add an existing user as a partner of `companyId`. Network-scoped: ensures an
 * ACTIVE partnership carrying `accessLevel` and lets the user switch into the
 * company. Self-service; no platform-owner needed.
 */
export const addPartnerUser = async (
  companyId: string,
  dto: AddPartnerUserDto
): Promise<{ userId: string; ownerCompanyId: string; partnerCompanyId: string; accessLevel: PartnerAccessLevel }> => {
  return apiFetch<{ userId: string; ownerCompanyId: string; partnerCompanyId: string; accessLevel: PartnerAccessLevel }>(
    `${BASE_URL}/${companyId}/partner-users`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  );
};

/**
 * Update a partnership
 */
export const updatePartnership = async (
  companyId: string,
  partnershipId: string,
  dto: UpdatePartnershipDto
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`,
    {
      method: 'PUT',
      body: JSON.stringify(dto),
    }
  );
};

/**
 * Terminate a partnership
 */
export const terminatePartnership = async (
  companyId: string,
  partnershipId: string,
  reason?: string
): Promise<void> => {
  return apiFetch<void>(
    `${BASE_URL}/${companyId}/partnerships/${partnershipId}`,
    {
      method: 'DELETE',
      body: reason ? JSON.stringify({ reason }) : undefined,
    }
  );
};

/**
 * Update grantedPlugins for a partnership (super-admin only).
 * Endpoint: PUT /api/admin/partnerships/:partnershipId/granted-plugins
 */
export const updatePartnershipGrantedPlugins = async (
  partnershipId: string,
  grantedPlugins: string[]
): Promise<EnrichedPartnership> => {
  return apiFetch<EnrichedPartnership>(
    `/api/admin/partnerships/${partnershipId}/granted-plugins`,
    {
      method: 'PUT',
      body: JSON.stringify({ grantedPlugins }),
    }
  );
};
